import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { maturationService } from '../../../services/maturationService';
import MeasurementGuidance from './MeasurementGuidance';

const MeasurementEntry = ({ child, onComplete, onCancel }) => {
  const [showGuidance, setShowGuidance] = useState(true);
  const [entryMode, setEntryMode] = useState('single'); // 'single' or 'bulk'
  const [formData, setFormData] = useState({
    measurementDatetime: new Date()?.toISOString()?.slice(0, 16),
    standingHeightCm: '',
    bodyMassKg: '',
    sittingHeightCm: '',
    heightReplicate1Cm: '',
    heightReplicate2Cm: '',
    measurementNotes: ''
  });
  const [bulkEntries, setBulkEntries] = useState([
    { date: '', height: '', bodyMass: '', sittingHeight: '', notes: '' },
    { date: '', height: '', bodyMass: '', sittingHeight: '', notes: '' },
    { date: '', height: '', bodyMass: '', sittingHeight: '', notes: '' }
  ]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [recentMeasurements, setRecentMeasurements] = useState([]);

  useEffect(() => {
    loadRecentMeasurements();
  }, [child?.id]);

  const loadRecentMeasurements = async () => {
    try {
      const measurements = await maturationService?.getMeasurements(child?.id);
      setRecentMeasurements(measurements || []);
    } catch (err) {
      console.error('Error loading measurements:', err);
    }
  };

  const checkForImplausiblePatterns = (newHeight) => {
    const newWarnings = [];
    
    if (recentMeasurements?.length > 0) {
      const sorted = [...recentMeasurements]?.sort((a, b) => 
        new Date(b?.measurementDatetime) - new Date(a?.measurementDatetime)
      );
      const lastMeasurement = sorted?.[0];
      const lastHeight = parseFloat(lastMeasurement?.standingHeightCm);
      const heightDiff = newHeight - lastHeight;
      
      const daysSinceLast = Math.abs(
        (new Date() - new Date(lastMeasurement?.measurementDatetime)) / (1000 * 60 * 60 * 24)
      );
      
      // Check for implausible negative growth
      if (heightDiff < -0.5) {
        newWarnings?.push({
          type: 'measurement_technique',
          message: 'Check your measurement technique',
          detail: 'Height appears to have decreased significantly. Ensure consistent measurement method.'
        });
      }
      
      // Check for implausible rapid growth
      if (daysSinceLast <= 30 && heightDiff > 2.0) {
        newWarnings?.push({
          type: 'measurement_technique',
          message: 'Check your measurement technique',
          detail: 'Height increase seems unusually large for this time period. Verify measurement accuracy.'
        });
      }
    }
    
    return newWarnings;
  };

  const checkForPersistentPain = (notes) => {
    const painKeywords = ['pain', 'hurt', 'ache', 'sore', 'discomfort', 'tender'];
    const hasPainMention = painKeywords?.some(keyword => 
      notes?.toLowerCase()?.includes(keyword)
    );
    
    if (hasPainMention && recentMeasurements?.length > 0) {
      // Check last 3 measurements for pain mentions
      const sorted = [...recentMeasurements]?.sort((a, b) => 
        new Date(b?.measurementDatetime) - new Date(a?.measurementDatetime)
      );
      const recentWithPain = sorted?.slice(0, 3)?.filter(m => {
        const notes = m?.measurementNotes?.toLowerCase() || '';
        return painKeywords?.some(keyword => notes?.includes(keyword));
      });
      
      if (recentWithPain?.length >= 2) {
        // Check if pain mentions span more than 14 days
        const oldestPainDate = new Date(recentWithPain?.[recentWithPain?.length - 1]?.measurementDatetime);
        const daysSinceFirstPain = Math.abs((new Date() - oldestPainDate) / (1000 * 60 * 60 * 24));
        
        if (daysSinceFirstPain > 14) {
          return {
            type: 'persistent_pain',
            message: 'Persistent pain should be assessed by a qualified professional.',
            detail: 'Pain has been mentioned in multiple measurements over more than 14 days.'
          };
        }
      }
    }
    
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    const newWarnings = [];

    if (!formData?.standingHeightCm) {
      newErrors.standingHeightCm = 'Standing height is required';
    } else if (parseFloat(formData?.standingHeightCm) < 50 || parseFloat(formData?.standingHeightCm) > 250) {
      newErrors.standingHeightCm = 'Height must be between 50 and 250 cm';
    } else {
      // Check for implausible patterns
      const patternWarnings = checkForImplausiblePatterns(parseFloat(formData?.standingHeightCm));
      newWarnings?.push(...patternWarnings);
    }

    if (formData?.heightReplicate1Cm && formData?.heightReplicate2Cm) {
      const diff = Math.abs(parseFloat(formData?.heightReplicate1Cm) - parseFloat(formData?.heightReplicate2Cm));
      if (diff > 2) {
        newErrors.heightReplicate2Cm = 'Replicates differ by more than 2cm. Please re-measure.';
      } else if (diff > 0.5) {
        newWarnings?.push({
          type: 'replicate_difference',
          message: 'Replicate difference is larger than recommended',
          detail: `Difference: ${diff?.toFixed(2)}cm. Aim for ≤0.5cm for best accuracy.`
        });
      }
    }
    
    // Check for persistent pain mentions
    if (formData?.measurementNotes?.trim()) {
      const painWarning = checkForPersistentPain(formData?.measurementNotes);
      if (painWarning) {
        newWarnings?.push(painWarning);
      }
    }

    setErrors(newErrors);
    setWarnings(newWarnings);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateBulkEntries = () => {
    const newErrors = {};
    const validEntries = bulkEntries?.filter(entry => entry?.date && entry?.height);
    
    if (validEntries?.length === 0) {
      newErrors.bulk = 'Please enter at least one measurement with date and height';
    }
    
    validEntries?.forEach((entry, idx) => {
      if (parseFloat(entry?.height) < 50 || parseFloat(entry?.height) > 250) {
        newErrors[`bulk_${idx}_height`] = 'Height must be between 50 and 250 cm';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await maturationService?.createMeasurement({
        childId: child?.id,
        measurementDatetime: formData?.measurementDatetime,
        timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
        standingHeightCm: parseFloat(formData?.standingHeightCm),
        bodyMassKg: formData?.bodyMassKg ? parseFloat(formData?.bodyMassKg) : null,
        sittingHeightCm: formData?.sittingHeightCm ? parseFloat(formData?.sittingHeightCm) : null,
        heightReplicate1Cm: formData?.heightReplicate1Cm ? parseFloat(formData?.heightReplicate1Cm) : null,
        heightReplicate2Cm: formData?.heightReplicate2Cm ? parseFloat(formData?.heightReplicate2Cm) : null,
        measurementNotes: formData?.measurementNotes
      });
      onComplete();
    } catch (err) {
      console.error('Error saving measurement:', err);
      setErrors({ submit: err?.message || 'Failed to save measurement' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateBulkEntries()) return;

    setIsSubmitting(true);
    try {
      const validEntries = bulkEntries?.filter(entry => entry?.date && entry?.height);
      
      // Submit all entries
      for (const entry of validEntries) {
        await maturationService?.createMeasurement({
          childId: child?.id,
          measurementDatetime: new Date(entry?.date)?.toISOString(),
          timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
          standingHeightCm: parseFloat(entry?.height),
          bodyMassKg: entry?.bodyMass ? parseFloat(entry?.bodyMass) : null,
          sittingHeightCm: entry?.sittingHeight ? parseFloat(entry?.sittingHeight) : null,
          heightReplicate1Cm: null,
          heightReplicate2Cm: null,
          measurementNotes: entry?.notes || ''
        });
      }
      
      onComplete();
    } catch (err) {
      console.error('Error saving bulk measurements:', err);
      setErrors({ submit: err?.message || 'Failed to save measurements' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addBulkEntry = () => {
    setBulkEntries([...bulkEntries, { date: '', height: '', bodyMass: '', sittingHeight: '', notes: '' }]);
  };

  const removeBulkEntry = (index) => {
    if (bulkEntries?.length > 1) {
      setBulkEntries(bulkEntries?.filter((_, idx) => idx !== index));
    }
  };

  const updateBulkEntry = (index, field, value) => {
    const updated = [...bulkEntries];
    updated[index][field] = value;
    setBulkEntries(updated);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="bg-primary/10 p-4 rounded-full mr-4">
              <Icon name="Ruler" className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Add Measurement</h1>
              <p className="text-text-secondary">For {child?.nickname}</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowGuidance(!showGuidance)}
            variant="outline"
            size="sm"
            iconName={showGuidance ? 'ChevronUp' : 'ChevronDown'}
          >
            {showGuidance ? 'Hide' : 'Show'} Guidance
          </Button>
        </div>

        {showGuidance && <MeasurementGuidance />}

        <div className="flex gap-2 mb-6 mt-6">
          <Button
            onClick={() => setEntryMode('single')}
            variant={entryMode === 'single' ? 'primary' : 'outline'}
            size="sm"
            className="flex-1"
          >
            Single Measurement
          </Button>
          <Button
            onClick={() => setEntryMode('bulk')}
            variant={entryMode === 'bulk' ? 'primary' : 'outline'}
            size="sm"
            className="flex-1"
          >
            Bulk Retrospective Entry
          </Button>
        </div>

        {warnings?.length > 0 && (
          <div className="space-y-3 mt-6">
            {warnings?.map((warning, idx) => (
              <div 
                key={idx} 
                className={`border rounded-lg p-4 ${
                  warning?.type === 'persistent_pain' ?'bg-error/10 border-error' :'bg-warning/10 border-warning'
                }`}
              >
                <div className="flex items-start">
                  <Icon 
                    name="AlertTriangle" 
                    className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                      warning?.type === 'persistent_pain' ? 'text-error' : 'text-warning'
                    }`} 
                  />
                  <div>
                    <p className="font-medium text-text-primary mb-1">{warning?.message}</p>
                    <p className="text-sm text-text-secondary">{warning?.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {entryMode === 'single' ? (
          <>
            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              <Input
                label="Measurement Date and Time"
                type="datetime-local"
                value={formData?.measurementDatetime}
                onChange={(e) => setFormData({ ...formData, measurementDatetime: e?.target?.value })}
                required
                description="Record the exact date and time of measurement"
              />

              <div className="bg-warning/10 border border-warning rounded-lg p-4">
                <div className="flex items-start">
                  <Icon name="AlertTriangle" className="w-5 h-5 text-warning mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-text-primary mb-1">Measurement Accuracy</p>
                    <p className="text-text-secondary">Small errors can create false growth spurts in the data. Please follow the guidance carefully.</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Height Replicate 1 (cm)"
                  type="number"
                  step="0.1"
                  value={formData?.heightReplicate1Cm}
                  onChange={(e) => setFormData({ ...formData, heightReplicate1Cm: e?.target?.value })}
                  placeholder="e.g., 152.3"
                  description="First measurement"
                />

                <Input
                  label="Height Replicate 2 (cm)"
                  type="number"
                  step="0.1"
                  value={formData?.heightReplicate2Cm}
                  onChange={(e) => setFormData({ ...formData, heightReplicate2Cm: e?.target?.value })}
                  error={errors?.heightReplicate2Cm}
                  placeholder="e.g., 152.5"
                  description="Second measurement"
                />
              </div>

              <Input
                label="Standing Height (cm)"
                type="number"
                step="0.1"
                value={formData?.standingHeightCm}
                onChange={(e) => setFormData({ ...formData, standingHeightCm: e?.target?.value })}
                error={errors?.standingHeightCm}
                required
                placeholder="e.g., 152.4"
                description="If replicates provided, mean will be calculated automatically"
              />

              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  label="Body Mass (kg) - Optional"
                  type="number"
                  step="0.1"
                  value={formData?.bodyMassKg}
                  onChange={(e) => setFormData({ ...formData, bodyMassKg: e?.target?.value })}
                  placeholder="e.g., 45.2"
                />

                <Input
                  label="Sitting Height (cm) - Optional"
                  type="number"
                  step="0.1"
                  value={formData?.sittingHeightCm}
                  onChange={(e) => setFormData({ ...formData, sittingHeightCm: e?.target?.value })}
                  placeholder="e.g., 78.5"
                />
              </div>

              <Input
                label="Measurement Notes - Optional"
                type="text"
                value={formData?.measurementNotes}
                onChange={(e) => setFormData({ ...formData, measurementNotes: e?.target?.value })}
                placeholder="Any observations or notes about this measurement"
              />

              {errors?.submit && (
                <div className="bg-error/10 border border-error rounded-lg p-4">
                  <p className="text-error text-sm">{errors?.submit}</p>
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button 
                  type="button"
                  onClick={onCancel}
                  variant="outline"
                  iconName="X"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  loading={isSubmitting}
                  iconName="Check"
                >
                  Save Measurement
                </Button>
              </div>
            </form>
          </>
        ) : (
          <form onSubmit={handleBulkSubmit} className="space-y-6 mt-6">
            <div className="bg-info/10 border border-info rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="Info" className="w-5 h-5 text-info mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-text-primary mb-1">Bulk Retrospective Entry</p>
                  <p className="text-text-secondary">Enter multiple past measurements at once. Only date and height are required for each entry. Body mass, sitting height, and notes are optional.</p>
                </div>
              </div>
            </div>

            {errors?.bulk && (
              <div className="bg-error/10 border border-error rounded-lg p-4">
                <p className="text-error text-sm">{errors?.bulk}</p>
              </div>
            )}

            <div className="space-y-4">
              {bulkEntries?.map((entry, idx) => (
                <div key={idx} className="bg-surface border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-text-primary">Measurement {idx + 1}</h3>
                    {bulkEntries?.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeBulkEntry(idx)}
                        variant="ghost"
                        size="sm"
                        iconName="X"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Input
                      label="Date"
                      type="date"
                      value={entry?.date}
                      onChange={(e) => updateBulkEntry(idx, 'date', e?.target?.value)}
                      required
                    />
                    <Input
                      label="Height (cm)"
                      type="number"
                      step="0.1"
                      value={entry?.height}
                      onChange={(e) => updateBulkEntry(idx, 'height', e?.target?.value)}
                      error={errors?.[`bulk_${idx}_height`]}
                      placeholder="e.g., 152.4"
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <Input
                      label="Body Mass (kg) - Optional"
                      type="number"
                      step="0.1"
                      value={entry?.bodyMass}
                      onChange={(e) => updateBulkEntry(idx, 'bodyMass', e?.target?.value)}
                      placeholder="e.g., 45.2"
                    />
                    <Input
                      label="Sitting Height (cm) - Optional"
                      type="number"
                      step="0.1"
                      value={entry?.sittingHeight}
                      onChange={(e) => updateBulkEntry(idx, 'sittingHeight', e?.target?.value)}
                      placeholder="e.g., 78.5"
                    />
                  </div>
                  <div className="mt-3">
                    <Input
                      label="Notes (optional)"
                      type="text"
                      value={entry?.notes}
                      onChange={(e) => updateBulkEntry(idx, 'notes', e?.target?.value)}
                      placeholder="Optional notes"
                    />
                  </div>
                </div>
              ))}
            </div>

            <Button
              type="button"
              onClick={addBulkEntry}
              variant="outline"
              iconName="Plus"
              className="w-full"
            >
              Add Another Measurement
            </Button>

            {errors?.submit && (
              <div className="bg-error/10 border border-error rounded-lg p-4">
                <p className="text-error text-sm">{errors?.submit}</p>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button 
                type="button"
                onClick={onCancel}
                variant="outline"
                iconName="X"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                loading={isSubmitting}
                iconName="Check"
              >
                Save All Measurements
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MeasurementEntry;